import { ref, type InjectionKey, type Ref } from "vue"
import { defineStore } from "pinia"
import type { Opportunity, Project, ProjectInput } from "@/model/DataModel.ts"
import { UseRootStore, type TableType } from "@/model/RootStore"
import { OpportunityType } from "@/graphql"
import { useSearchContextStore } from "@/stores/searchContextStore"
import * as apiClient from "@/utils/ApiClient"
import AppRouter from "@/AppRouter"
import { firstQueryValue } from "@/utils/routing"

export type ViewConfigColumn = {
	key: string
	visible: boolean
	// which group the column belongs to (e.g. main table vs. details panel); absent for tables without groups
	group?: string
}

export type ViewConfigSortLevel = {
	key: string
	order: "asc" | "desc"
}

export type ViewConfig = {
	columns: ViewConfigColumn[]
	sortLevels?: ViewConfigSortLevel[]
}

type Stringable = {
	toString(): string
}

type ProjectFetchOptions = {
	includePeople?: boolean
}

const ProjectQueryParamName = "project"

function getQueryParam(name: string): string | null {
	return new URLSearchParams(window.location.search).get(name)
}

function setQueryParam(name: string, value: Stringable): void {
	const url = new URL(window.location.href)
	url.searchParams.set(name, String(value))
	window.history.pushState(null, "", url)
	syncProjectQueryParam()
}

// Reactive mirror of the `project` query param. `window.location.search` itself isn't tracked
// by Vue, so a computed reading it directly freezes at whatever value it saw first; reading this
// ref (even indirectly, through isHuntingMode()) makes that computed re-evaluate on navigation.
// Kept in sync on every URL change this module makes, plus browser back/forward.
const projectQueryParam = ref<string | null>(getQueryParam(ProjectQueryParamName))

function syncProjectQueryParam(): void {
	projectQueryParam.value = getQueryParam(ProjectQueryParamName)
}

if (typeof window !== "undefined") {
	window.addEventListener("popstate", syncProjectQueryParam)
}

// hunting mode is a "project" scoped view (for now let's use ?project=<id> condition in the URL).
// Checked against both sources: the router's own reactive route (kept live by vue-router itself
// on every push/replace/back-forward it drives) and our manual mirror (kept live for URL changes
// this module makes directly via history.pushState, which vue-router never observes).
export function isHuntingMode(): boolean {
	return (
		projectQueryParam.value !== null ||
		firstQueryValue(AppRouter.currentRoute.value.query[ProjectQueryParamName]) != null
	)
}

// reads a localStorage value, but only in map mode — hunting mode never sees a persisted value
export function getMapModeStorageItem(key: string): string | null {
	if (isHuntingMode()) return null
	try {
		return localStorage.getItem(key)
	} catch (error) {
		console.warn("error:", error)
		return null
	}
}

// writes (or clears, when value is null) a localStorage value, a no-op in hunting mode
export function setMapModeStorageItem(key: string, value: string | null): void {
	if (isHuntingMode()) return
	try {
		if (value === null) localStorage.removeItem(key)
		else localStorage.setItem(key, value)
	} catch (error) {
		console.warn("error:", error)
	}
}

export function createProjectStore() {
	return defineStore("project", () => {
		const loading = ref(false)
		const ready = ref(false)
		const currentProject = ref<Project | null>(null)

		function apiToken(): string {
			return UseRootStore().userApiToken
		}

		async function updateProjectSearchContext(projectId: string, projectName: string): Promise<void> {
			const searchContextStore = useSearchContextStore()
			const searchContext = await searchContextStore.updateProjectSearchContext(projectId, projectName)
			if (currentProject.value?.id === projectId) {
				currentProject.value = {
					...currentProject.value,
					searchContext,
				}
			}
		}

		function mergeProject(project: Project | null): void {
			const cached = currentProject.value
			if (project && cached?.id === project.id) {
				// Metadata requests can omit people; only supplied lists replace loaded assignments.
				currentProject.value = {
					...project,
					brokers: project.brokers === undefined ? cached.brokers : project.brokers,
					prospectors: project.prospectors === undefined ? cached.prospectors : project.prospectors,
					clients: project.clients === undefined ? cached.clients : project.clients,
				}
			} else {
				currentProject.value = project
			}
		}

		async function fetchProject(
			loadProject: (id: string) => Promise<Project | null>,
			shouldSetReady = true
		): Promise<Ref<Project | null>> {
			const id = getQueryParam(ProjectQueryParamName)
			if (id === null) {
				currentProject.value = null
				if (shouldSetReady) ready.value = true
				return currentProject
			}

			loading.value = true
			try {
				mergeProject(await loadProject(id))
			} finally {
				loading.value = false
				if (shouldSetReady) ready.value = true
			}

			return currentProject
		}

		async function fetchCurrentProjectMetadata(options: ProjectFetchOptions = {}): Promise<Ref<Project | null>> {
			return fetchProject(id => apiClient.fetchProjectMetadata(apiToken(), id, options))
		}

		async function fetchProjectMetadata(projectId: string, options: ProjectFetchOptions = {}): Promise<Project | null> {
			loading.value = true
			try {
				const project = await apiClient.fetchProjectMetadata(apiToken(), projectId, options)
				mergeProject(project)
				return project
			} finally {
				loading.value = false
			}
		}

		async function createProject(
			name: string,
			ccaContractHubspotId: string,
			brokerIds: number[],
			expiryDate?: string,
			prospectorIds?: number[]
		): Promise<Project | null> {
			const project: ProjectInput = {
				name,
				ccaContractHubspotId,
				brokerIds,
				prospectorIds,
				expiryDate,
			}

			loading.value = true
			try {
				currentProject.value = await apiClient.createProject(apiToken(), project)
				setQueryParam(ProjectQueryParamName, currentProject.value?.id ?? "")
				if (currentProject.value?.id != null) {
					await updateProjectSearchContext(currentProject.value.id, name)
				}
			} finally {
				loading.value = false
			}

			return currentProject.value
		}

		async function updateProject(
			id: string,
			name: string,
			ccaContractHubspotId: string,
			brokerIds: number[],
			expiryDate?: string,
			prospectorIds?: number[],
			clientIds?: number[]
		): Promise<Project | null> {
			const project: ProjectInput = {
				id,
				name,
				ccaContractHubspotId,
				brokerIds,
				prospectorIds,
				clientIds,
				expiryDate,
			}

			loading.value = true
			try {
				currentProject.value = await apiClient.updateProject(apiToken(), project)
				if (currentProject.value?.id != null) {
					await updateProjectSearchContext(currentProject.value.id, name)
				}
			} finally {
				loading.value = false
			}

			return currentProject.value
		}

		async function createWorkspace(userProfileId: number): Promise<Project | null> {
			loading.value = true
			try {
				return await apiClient.createWorkspace(apiToken(), userProfileId)
			} finally {
				loading.value = false
			}
		}

		async function updateViewConfig(patch: Partial<ViewConfig>): Promise<void> {
			const project = currentProject.value
			if (!project) return
			const existing = (project.viewConfig as ViewConfig | null | undefined) ?? {}
			const updated = await apiClient.updateProject(apiToken(), {
				id: project.id,
				viewConfig: { ...existing, ...patch },
			})
			if (updated && currentProject.value) {
				currentProject.value = { ...currentProject.value, viewConfig: updated.viewConfig }
			}
		}

		// full reload is needed since currentProject is only ever resolved on page load
		function switchProject(id: string | null): void {
			const url = new URL(window.location.href)
			if (id) url.searchParams.set(ProjectQueryParamName, id)
			else url.searchParams.delete(ProjectQueryParamName)
			window.location.href = url.toString()
		}

		// tableType: pass a TableType to land on that table, null to land on the map, or omit to
		// keep whatever view (?table=) was already in the URL.
		function exitProject(tableType?: TableType | null): void {
			const url = new URL(window.location.href)
			url.searchParams.delete(ProjectQueryParamName)
			if (tableType === null) {
				url.searchParams.delete("table")
			} else if (tableType) {
				url.searchParams.set("table", tableType)
			}

			// targets only exist within a project; a stale ?opp= would reselect one after exploration reload
			const rootStore = UseRootStore()
			const selected = rootStore.opportunities.find(
				(opportunity: Opportunity) => String(opportunity.id) === String(rootStore.selectedOpportunityId)
			)
			if (selected?.type === OpportunityType.OpportunityTarget) {
				url.searchParams.delete("opp")
			}

			window.location.href = url.toString()
		}

		async function addComment(opportunityTargetId: number, comment: string): Promise<void> {
			loading.value = true
			try {
				const updatedTarget = await apiClient.addOpportunityTargetComment(apiToken(), opportunityTargetId, comment)
				const targets = currentProject.value?.opportunityTargets
				if (!targets) return

				const index = targets.findIndex(target => target.id === updatedTarget.id)
				if (index >= 0) {
					targets[index] = updatedTarget
				} else {
					targets.push(updatedTarget)
				}
			} finally {
				loading.value = false
			}
		}

		return {
			loading,
			ready,
			currentProject,
			fetchCurrentProjectMetadata,
			fetchProjectMetadata,
			createProject,
			updateProject,
			createWorkspace,
			updateProjectSearchContext,
			updateViewConfig,
			switchProject,
			exitProject,
			addComment,
		}
	})
}

type UseProjectStore = ReturnType<typeof createProjectStore>

export const ProjectStoreKey = Symbol("useProjectStore") as InjectionKey<UseProjectStore>
